import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { motion } from "framer-motion";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";

const PaymentVerificationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState("");
  const [attemptCount, setAttemptCount] = useState(0);
  const [maxAttempts] = useState(3);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");

  const handleModalClose = () => {
    setShowSuccessModal(false);
    setShowCancelModal(false);
    if (showSuccessModal) {
      Object.keys(Cookies.get()).forEach((cookie) => {
        Cookies.remove(cookie);
      });
      navigate("/complete");
    } else {
      const isIamportPayment = searchParams.get("imp_uid") !== null;
      if (isIamportPayment) {
        navigate("/payment/creditcard");
      } else {
        navigate("/payment");
      }
    }
  };

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // URL에서 paymentId 추출
        let paymentId;
        const impSuccess = searchParams.get("imp_success");

        if (impSuccess === "true") {
          paymentId = searchParams.get("merchant_uid");
        } else {
          paymentId =
            searchParams.get("paymentId") || searchParams.get("imp_uid");
        }

        if (!paymentId) {
          setError("결제 정보가 없습니다.");
          setTimeout(() => {
            // 카카오페이 결제 페이지로 돌아가기 (기본값)
            navigate("/payment");
          }, 3000);
          return;
        }

        // 토큰 확인
        const token = Cookies.get(
          API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN
        );
        if (!token) {
          throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
        }

        // 결제 방식에 따른 portoneVersion 설정
        const isIamportPayment = searchParams.get("imp_uid") !== null;
        const portoneVersion = isIamportPayment ? "v1" : "v2";

        // 폴링으로 결제 상태 확인 (최대 15초)
        const pollInterval = 5000; // 5초
        let attempts = 0;
        let paymentConfirmed = false;

        while (attempts < maxAttempts && !paymentConfirmed) {
          try {
            // 현재 시도 횟수 업데이트
            setAttemptCount(attempts + 1);

            // 결제 확인 API 호출
            const response = await fetch(
              `${API_ENDPOINTS.BASE_URL}/payments/portonePaymentId/${paymentId}?portoneVersion=${portoneVersion}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${token}`,
                },
              }
            );

            if (response.status === 200) {
              const verificationResult = await response.json();
              console.log("verificationResult", verificationResult);
              // 결제가 정상적으로 완료된 경우
              if (verificationResult.status === "PAID") {
                paymentConfirmed = true;
                setShowSuccessModal(true);
                return;
              }
            } else if (response.status === 404) {
              // 웹훅 처리가 아직 안됨 - 계속 대기
              console.log(
                `결제 확인 시도 ${
                  attempts + 1
                }/${maxAttempts} - 웹훅 처리 대기 중`
              );
            } else {
              throw new Error(
                `결제 확인 중 오류가 발생했습니다. (상태코드: ${response.status})`
              );
            }
          } catch (fetchError) {
            console.error(
              `결제 확인 API 오류 (시도 ${attempts + 1}):`,
              fetchError
            );
            // API 오류는 로그만 남기고 계속 시도
          }

          attempts++;

          // 마지막 시도가 아니면 대기
          if (attempts < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
          }
        }

        // 최대 시도 횟수를 초과했거나 결제가 확인되지 않은 경우
        if (!paymentConfirmed) {
          console.log("결제 확인 시간 초과 - 결제 취소 진행");

          // 결제 취소 API 호출
          const cancelSuccess = await cancelPayment(
            paymentId,
            portoneVersion,
            "결제 확인 시간 초과로 인한 취소"
          );

          if (cancelSuccess) {
            setModalMessage("결제 확인 시간이 초과되어 결제가 취소되었습니다.");
          } else {
            setModalMessage("결제 확인에 실패했습니다.");
          }
          setShowCancelModal(true);
        }
      } catch (error) {
        console.error("결제 확인 중 오류:", error);
        setModalMessage(error.message || "결제 확인 중 오류가 발생했습니다.");
        setShowCancelModal(true);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [searchParams, navigate]);

  // 결제 취소 API 호출
  const cancelPayment = async (
    paymentId,
    portoneVersion,
    reason = "결제 확인 실패로 인한 취소"
  ) => {
    try {
      const token = Cookies.get(API_ENDPOINTS.COOKIE_CONFIG.KEYS.ACCESS_TOKEN);
      if (!token) {
        throw new Error("인증 토큰이 없습니다.");
      }

      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/payments/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentId: paymentId,
            portoneVersion: portoneVersion,
            reason: reason,
          }),
        }
      );

      if (!response.ok) {
        console.error("결제 취소 실패");
        return false;
      }

      return true;
    } catch (error) {
      console.error("결제 취소 중 오류:", error);
      return false;
    }
  };

  return (
    <PageBackground>
      <Container
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <Content>
          {isVerifying ? (
            <VerifyingContainer>
              <Spinner />
              <Title>결제 확인 중</Title>
              <Description>
                결제 정보를 확인하고 있습니다.
                <br />
                잠시만 기다려 주세요.
              </Description>
              {attemptCount > 0 && (
                <ProgressDescription>
                  확인 시도: {attemptCount} / {maxAttempts}
                  <br />
                  (약 {Math.ceil((maxAttempts * 5) / 60)}분 소요 예정)
                </ProgressDescription>
              )}
              <ReturnButton
                onClick={() => {
                  const isIamportPayment = searchParams.get("imp_uid") !== null;
                  if (isIamportPayment) {
                    navigate("/payment/creditcard");
                  } else {
                    navigate("/payment");
                  }
                }}
              >
                결제 페이지로 돌아가기
              </ReturnButton>
            </VerifyingContainer>
          ) : error ? (
            <ErrorContainer>
              <ErrorIcon>⚠️</ErrorIcon>
              <Title>결제 확인 실패</Title>
              <Description>{error}</Description>
              <SubDescription>잠시 후 결제 페이지로 돌아갑니다.</SubDescription>
            </ErrorContainer>
          ) : null}
        </Content>
      </Container>

      {/* 성공 모달 */}
      {showSuccessModal && (
        <ModalOverlay>
          <ModalContainer>
            <ModalContent>
              <ModalTitle>결제 성공</ModalTitle>
              <ModalText>
                결제가 성공적으로 완료되었습니다.
                <br />
                완료 페이지로 이동합니다.
              </ModalText>
              <ModalButton onClick={handleModalClose}>확인</ModalButton>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}

      {/* 취소 모달 */}
      {showCancelModal && (
        <ModalOverlay>
          <ModalContainer>
            <ModalContent>
              <ModalTitle>결제 실패</ModalTitle>
              <ModalText>
                {modalMessage}
                <br />
                결제 페이지로 이동합니다.
              </ModalText>
              <ModalButton onClick={handleModalClose}>확인</ModalButton>
            </ModalContent>
          </ModalContainer>
        </ModalOverlay>
      )}
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f8f8f8;
`;

const Container = styled(motion.div)`
  width: 100%;
  max-width: 400px;
  padding: 40px 20px;
  margin: 0 auto;
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;

  @media (max-width: 768px) {
    max-width: 100%;
    border-radius: 0;
    box-shadow: none;
    min-height: 100vh;
    display: flex;
    align-items: center;
    margin: 0;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
`;

const VerifyingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
`;

const Spinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0075ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #333;
  margin: 0;
`;

const Description = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  margin: 0;
`;

const SubDescription = styled.p`
  font-size: 14px;
  color: #999;
  line-height: 1.4;
  margin: 0;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
`;

const ProgressDescription = styled.p`
  font-size: 14px;
  color: #999;
  line-height: 1.4;
  margin: 0;
`;

const ReturnButton = styled.button`
  margin-top: 20px;
  padding: 12px 24px;
  border-radius: 8px;
  background-color: #0075ff;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0066dd;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 12px;
  width: 90%;
  max-width: 320px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #333;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const ModalButton = styled.button`
  padding: 12px 40px;
  border-radius: 8px;
  background-color: #0075ff;
  width: 100%;
  color: white;
  font-size: 16px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0066dd;
  }
`;

export default PaymentVerificationPage;
